import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FixedSizeList as List } from 'react-window';
import {
  DropdownWithSearchProps,
  Option,
} from 'components/core/DropdownWithSearch/DropdownWithSearch.types';
import { Styled } from '.';
import { MdKeyboardArrowDown, MdClose, MdSearch } from 'react-icons/md';
import { useTheme } from 'styled-components';
import {
  getOptionByValue,
  getControlStyles,
  sizeConfigMap,
} from 'components/core/DropdownWithSearch/DropdownWithSearch.helper';

const OPTION_HEIGHT_DEFAULT = 36;
const MENU_SEARCH_BAR_HEIGHT = 43;
const MENU_BORDER_AND_MARGIN = 6;

const DropdownWithSearch: React.FC<DropdownWithSearchProps> = ({
  options = [],
  placeholder = 'Select...',
  value,
  onChange,
  isClearable = true,
  width = '100%',
  height = 'auto',
  selectedValue = '',
  background,
  hasError,
  size = 'md',
  formatOptionLabel,
  optionHeight,
  menuSearchable = false,
  menuSearchPlaceholder = 'Search...',
  noBorder = true,
  disabled = false,
}) => {
  const [internalValue, setInternalValue] = useState<Option | null>(
    value ?? getOptionByValue(options, selectedValue)
  );
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDirection, setOpenDirection] = useState<'bottom' | 'top'>(
    'bottom'
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const controlInputRef = useRef<HTMLInputElement>(null);

  const scTheme = useTheme();
  const sizeStyles = sizeConfigMap[size];
  const controlStyles = getControlStyles(background);
  const useExpandedLayout = !!formatOptionLabel;
  const rowHeight = optionHeight ?? OPTION_HEIGHT_DEFAULT;

  const currentValue = value === undefined ? internalValue : value;

  useEffect(() => {
    setInternalValue(value ?? getOptionByValue(options, selectedValue));
  }, [value, options, selectedValue]);

  // Close menu on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Auto-focus the search input when menu opens
  useEffect(() => {
    if (isOpen && menuSearchable) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
    if (isOpen && !menuSearchable) {
      requestAnimationFrame(() => controlInputRef.current?.focus());
    }
  }, [isOpen, menuSearchable]);

  const handleSelect = useCallback(
    (option: Option) => {
      const isControlled = value !== undefined;
      if (!isControlled) {
        setInternalValue(option);
      }
      onChange?.(option.value);
      setIsOpen(false);
      setSearchTerm('');
    },
    [value, onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const isControlled = value !== undefined;
      if (!isControlled) {
        setInternalValue(null);
      }
      onChange?.(undefined);
      setSearchTerm('');
      setIsOpen(true);
    },
    [value, onChange]
  );

  const toggleMenu = useCallback(() => {
    setIsOpen(prev => {
      if (prev) setSearchTerm('');
      return !prev;
    });
  }, []);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lower = searchTerm.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(lower));
  }, [options, searchTerm]);

  const listHeight = Math.min(
    sizeStyles.menuMaxHeight,
    filteredOptions.length * rowHeight
  );

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedHeight =
      Math.min(sizeStyles.menuMaxHeight, options.length * rowHeight) +
      (menuSearchable ? MENU_SEARCH_BAR_HEIGHT : 0) +
      MENU_BORDER_AND_MARGIN;
    setOpenDirection(
      spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove
        ? 'bottom'
        : 'top'
    );
    // Only recompute when the menu opens, not when options/size change mid-session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const errorBorder = hasError ? '1px solid rgba(212, 63, 88, 1)' : undefined;
  const controlBorder =
    errorBorder ??
    (isOpen ? '1px solid rgba(32, 154, 214, 1)' : controlStyles.border);
  const withBorderStyle = errorBorder ?? '1px solid rgba(32, 154, 214, 1)';

  const renderOptionContent = (option: Option) => {
    if (formatOptionLabel) return formatOptionLabel(option);
    return option.label;
  };

  return (
    <Styled.Container
      ref={containerRef}
      data-testid="dropdown-with-search"
      $width={width}
      $height={height}
      style={{ position: 'relative' }}
    >
      {/* Control */}
      <div
        role="combobox"
        data-testid="dropdown-control"
        aria-expanded={isOpen}
        aria-controls="dropdown-menu"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : toggleMenu}
        onKeyDown={
          disabled
            ? undefined
            : e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleMenu();
                }
              }
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          border: noBorder ? controlBorder : withBorderStyle,
          background: disabled ? '#f5f5f5' : controlStyles.background,
          borderRadius: 4,
          minHeight: sizeStyles.minHeight,
          fontSize: sizeStyles.fontSize,
          padding: sizeStyles.controlPadding,
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxSizing: 'border-box',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div
          style={{
            flex: 1,
            padding: sizeStyles.valueContainerPadding,
            fontSize: sizeStyles.fontSize,
            overflow: useExpandedLayout ? 'visible' : 'hidden',
            minWidth: 0,
          }}
        >
          {/* Inline search input for non-menuSearchable mode */}
          {!menuSearchable && isOpen ? (
            <input
              ref={controlInputRef}
              data-testid="dropdown-inline-search"
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={currentValue?.label ?? placeholder}
              onClick={e => e.stopPropagation()}
              style={{
                border: 'none',
                outline: 'none',
                width: '100%',
                fontSize: sizeStyles.fontSize,
                background: 'transparent',
                color: '#1a1a1a',
                padding: 0,
              }}
            />
          ) : currentValue ? (
            <div
              style={{
                fontSize: sizeStyles.fontSize,
                padding: useExpandedLayout ? '6px 0' : undefined,
                ...(useExpandedLayout
                  ? {
                      maxWidth: '100%',
                      overflow: 'visible',
                      whiteSpace: 'normal' as const,
                    }
                  : {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' as const,
                    }),
              }}
            >
              {renderOptionContent(currentValue)}
            </div>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: sizeStyles.fontSize }}>
              {placeholder}
            </div>
          )}
        </div>

        {/* Clear button */}
        {isClearable && currentValue && (
          <div
            data-testid="dropdown-clear-btn"
            role="button"
            tabIndex={0}
            onClick={handleClear}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClear(e as unknown as React.MouseEvent);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 4px',
              cursor: 'pointer',
            }}
          >
            <MdClose
              size={16}
              color={scTheme?.palette?.grey44 ?? 'rgba(0,0,0,0.4)'}
            />
          </div>
        )}

        {/* Dropdown arrow */}
        <Styled.DropdownWrapper>
          <MdKeyboardArrowDown
            size={sizeStyles.indicatorSize}
            color={scTheme?.palette?.grey44 ?? 'rgba(0,0,0,0.6)'}
          />
        </Styled.DropdownWrapper>
      </div>

      {/* Menu */}
      {isOpen && (
        <div
          id="dropdown-menu"
          data-testid="dropdown-menu"
          style={{
            position: 'absolute',
            ...(openDirection === 'bottom'
              ? { top: '100%', marginTop: 4 }
              : { bottom: '100%', marginBottom: 4 }),
            left: 0,
            right: 0,
            background: '#ffffff',
            borderRadius: 4,
            border: '1px solid rgba(0,0,0,0.15)',
            boxShadow: '0px 4px 16px rgba(0,0,0,0.15)',
            zIndex: 10,
            overflow: 'hidden',
          }}
        >
          {/* In-menu search bar */}
          {menuSearchable && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                borderBottom: '1px solid #e5e8ec',
                gap: 8,
              }}
            >
              <MdSearch
                size={18}
                color="rgba(0,0,0,0.4)"
                style={{ flexShrink: 0 }}
              />
              <input
                ref={searchInputRef}
                data-testid="dropdown-menu-search"
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={menuSearchPlaceholder}
                onClick={e => e.stopPropagation()}
                style={{
                  border: 'none',
                  outline: 'none',
                  width: '100%',
                  fontSize: sizeStyles.fontSize,
                  background: 'transparent',
                  color: '#1a1a1a',
                }}
              />
            </div>
          )}

          {/* Options list */}
          {filteredOptions.length > 0 ? (
            <List
              height={listHeight}
              itemCount={filteredOptions.length}
              itemSize={rowHeight}
              width="100%"
            >
              {({ index, style }) => {
                const option = filteredOptions[index];
                const isSelected = currentValue?.value === option.value;
                return (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onClick={() => handleSelect(option)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(option);
                      }
                    }}
                    style={{
                      ...style,
                      display: 'flex',
                      alignItems: 'center',
                      textAlign: useExpandedLayout ? 'left' : 'center',
                      padding: useExpandedLayout
                        ? sizeStyles.optionPaddingCustomLabel
                        : sizeStyles.optionPadding,
                      fontSize: sizeStyles.fontSize,
                      background: isSelected ? '#f0f9ff' : '#ffffff',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                    onMouseEnter={e => {
                      if (!isSelected)
                        e.currentTarget.style.background =
                          'rgba(242,242,242,1)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = isSelected
                        ? '#f0f9ff'
                        : '#ffffff';
                    }}
                  >
                    <div style={{ width: '100%' }}>
                      {renderOptionContent(option)}
                    </div>
                  </div>
                );
              }}
            </List>
          ) : (
            <div
              style={{
                padding: '12px',
                textAlign: 'center',
                color: '#999',
                fontSize: sizeStyles.fontSize,
              }}
            >
              No options
            </div>
          )}
        </div>
      )}
    </Styled.Container>
  );
};

export default DropdownWithSearch;
